import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { 
  FileText, Loader2, Check, AlertCircle, 
  Edit2, ChevronDown, ChevronUp, Hotel, Calendar, 
  CheckCircle, XCircle, Plus, Trash2, Bot, PenLine,
  DollarSign, Upload, File, MapPin, ArrowRight, Sparkles
} from 'lucide-react';
import api from '../utils/api';

const CURRENCIES = [
  { value: 'INR', label: '₹ INR', symbol: '₹' },
  { value: 'USD', label: '$ USD', symbol: '$' },
  { value: 'EUR', label: '€ EUR', symbol: '€' },
];

const MEAL_PLANS = ['EP', 'CP', 'MAP', 'AP'];

/**
 * DMCPackageBuilder - Component for pasting and parsing DMC package content
 * Supports both AI parsing and Manual entry modes
 */
const DMCPackageBuilder = ({ 
  formData, 
  setFormData,
  handleInputChange,
}) => {
  // Default package data structure
  const defaultPackageData = {
    package_title: '',
    total_nights: 0,
    total_days: 0,
    accommodation_options: [
      {
        option_name: 'Option 1',
        hotels: [{ place: '', nights: 1, rooms: 1, hotel: '', room_type: '', meal_plan: 'CP' }]
      }
    ],
    pricing_options: [
      { option_name: 'Option 1', currency: 'INR', per_person: 0, extra_adult: 0, child_with_bed: 0, child_no_bed: 0, total_amount: 0 }
    ],
    itinerary: [{ day: 1, title: '', description: '' }],
    inclusions: [''],
    exclusions: [''],
    validity: '',
    vehicle: '',
    notes: '',
  };

  // Initialize from existing formData.dmc_content if available (for back navigation)
  const existingDmc = formData.dmc_content;
  
  // Entry mode: 'ai' or 'manual'
  const [entryMode, setEntryMode] = useState(existingDmc?.entry_mode || 'ai');
  
  // Local state for AI parsing - restore from existing data
  const [rawContent, setRawContent] = useState(existingDmc?.raw_input || '');
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState(null);
  
  // File upload state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [extractingFile, setExtractingFile] = useState(false);
  const fileInputRef = useRef(null);
  
  // Track if this is the initial mount (to prevent overwriting existing data)
  const isInitialMount = useRef(true);
  
  // Parsed/Manual data structure - restore from existing data or use defaults
  const [packageData, setPackageData] = useState(() => {
    if (existingDmc?.parsed && Object.keys(existingDmc.parsed).length > 0) {
      // Merge existing data with defaults to ensure all fields exist
      return {
        ...defaultPackageData,
        ...existingDmc.parsed,
        // Ensure arrays have at least default structure
        accommodation_options: existingDmc.parsed.accommodation_options?.length > 0 
          ? existingDmc.parsed.accommodation_options 
          : defaultPackageData.accommodation_options,
        pricing_options: existingDmc.parsed.pricing_options?.length > 0 
          ? existingDmc.parsed.pricing_options 
          : defaultPackageData.pricing_options,
        itinerary: existingDmc.parsed.itinerary?.length > 0 
          ? existingDmc.parsed.itinerary 
          : defaultPackageData.itinerary,
        inclusions: existingDmc.parsed.inclusions?.length > 0 
          ? existingDmc.parsed.inclusions 
          : defaultPackageData.inclusions,
        exclusions: existingDmc.parsed.exclusions?.length > 0 
          ? existingDmc.parsed.exclusions 
          : defaultPackageData.exclusions,
      };
    }
    return defaultPackageData;
  });
  
  // Editing states
  const [expandedItinerary, setExpandedItinerary] = useState(true);
  const [expandedRoute, setExpandedRoute] = useState(true);
  
  // Route state for DMC Package (display/edit only - not for calculation)
  const [route, setRoute] = useState(() => {
    // Try to derive route from existing accommodation data
    const existingHotels = existingDmc?.parsed?.accommodation_options?.[0]?.hotels || [];
    if (existingHotels.length > 0) {
      return existingHotels.map((h, idx) => ({
        id: `stop-${idx}`,
        place_name: h.place || '',
        nights: h.nights || 1,
      }));
    }
    return [{ id: 'stop-1', place_name: '', nights: 1 }];
  });

  // Add a new stop to route
  const addRouteStop = () => {
    const newId = `stop-${Date.now()}`;
    setRoute([...route, { id: newId, place_name: '', nights: 1 }]);
  };

  // Remove a stop from route
  const removeRouteStop = (index) => {
    if (route.length > 1) {
      setRoute(route.filter((_, i) => i !== index));
    }
  };

  // Update a route stop
  const updateRouteStop = (index, field, value) => {
    const updated = [...route];
    updated[index] = { ...updated[index], [field]: field === 'nights' ? parseInt(value) || 0 : value };
    setRoute(updated);
  };

  // Calculate total nights and days from route
  const totalNightsFromRoute = route.reduce((sum, stop) => sum + (parseInt(stop.nights) || 0), 0);
  const totalDaysFromRoute = totalNightsFromRoute + 1;

  // Auto-generate itinerary from route
  const autoGenerateItinerary = () => {
    const newItinerary = [];
    let dayNum = 1;
    
    // Process each stop - each night = one day
    route.forEach((stop, idx) => {
      const nights = parseInt(stop.nights) || 1;
      const isFirstStop = idx === 0;
      
      for (let n = 0; n < nights; n++) {
        const isFirstNightOfFirstStop = isFirstStop && n === 0;
        const arrivalCity = formData.arrival_city || route[0]?.place_name || 'Destination';
        
        newItinerary.push({
          day: dayNum,
          title: isFirstNightOfFirstStop 
            ? `Arrival at ${arrivalCity}`
            : `Day ${dayNum} - ${stop.place_name}`,
          overnight_location: stop.place_name,
          description: isFirstNightOfFirstStop
            ? `Arrive at ${arrivalCity}. Transfer to ${stop.place_name} and check-in. Rest of the day at leisure.`
            : `Explore ${stop.place_name}. Overnight stay at ${stop.place_name}.`
        });
        dayNum++;
      }
    });
    
    // Add departure day (after all nights are complete)
    const lastStop = route[route.length - 1];
    const departureCity = formData.departure_city || lastStop?.place_name || 'Destination';
    newItinerary.push({
      day: dayNum,
      title: `Departure from ${departureCity}`,
      overnight_location: 'Departure',
      description: `Check-out from hotel. Transfer to ${departureCity} for departure.`
    });

    // Update itinerary in packageData
    setPackageData(prev => ({
      ...prev,
      itinerary: newItinerary
    }));
  };

  // Calculate total amount based on passengers
  const calculateTotalAmount = (pricing) => {
    const adults = formData.no_adults || 0;
    const childrenWithBed = formData.no_children_with_bed || 0;
    const childrenNoBed = formData.no_children_no_bed || 0;
    
    if (adults === 0 && childrenWithBed === 0 && childrenNoBed === 0) {
      return pricing.total_amount || 0; // Return manual entry if no passengers
    }
    
    // Get room configuration for capacity calculation
    const doubleRooms = formData.room_config?.double_rooms || Math.max(1, Math.ceil(adults / 2));
    const singleRooms = formData.room_config?.single_rooms || 0;
    const twinSharingCapacity = (doubleRooms * 2) + singleRooms;
    
    // Adults up to twin sharing capacity use per_person rate
    const adultsInRooms = Math.min(adults, twinSharingCapacity);
    const extraAdultsCount = Math.max(0, adults - twinSharingCapacity);
    
    // If extra_adult rate is 0 or not set, use per_person rate for all adults
    const effectiveExtraAdultRate = (pricing.extra_adult || 0) > 0 ? pricing.extra_adult : (pricing.per_person || 0);
    
    // Calculate costs
    const adultCost = (pricing.per_person || 0) * adultsInRooms + effectiveExtraAdultRate * extraAdultsCount;
    const childWithBedCost = (pricing.child_with_bed || 0) > 0 
      ? (pricing.child_with_bed * childrenWithBed) 
      : ((pricing.per_person || 0) * 0.7 * childrenWithBed);
    const childNoBedCost = (pricing.child_no_bed || 0) > 0 
      ? (pricing.child_no_bed * childrenNoBed) 
      : ((pricing.per_person || 0) * 0.5 * childrenNoBed);
    
    return adultCost + childWithBedCost + childNoBedCost;
  };

  // Sync packageData to formData.dmc_content
  // Skip on initial mount to prevent overwriting existing data when navigating back
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return; // Skip sync on initial mount
    }
    
    // Calculate total nights/days from route or accommodation
    let totalNights = totalNightsFromRoute;
    if (totalNights === 0 && packageData.accommodation_options.length > 0) {
      totalNights = packageData.accommodation_options[0].hotels.reduce(
        (sum, h) => sum + (h.nights || 0), 0
      );
    }
    
    const updatedData = {
      ...packageData,
      total_nights: totalNights,
      total_days: totalNights + 1,
      route: route, // Include route in saved data
    };
    
    setFormData(prev => ({
      ...prev,
      dmc_content: {
        raw_input: rawContent,
        parsed: updatedData,
        entry_mode: entryMode,
        route: route, // Also store at top level for easy access
      },
      total_nights: totalNights,
      total_days: totalNights + 1,
    }));
  }, [packageData, rawContent, entryMode, route, totalNightsFromRoute, setFormData]);

  // Parse DMC content with AI
  const handleParse = async () => {
    if (!rawContent.trim()) {
      setParseError('Please paste the DMC package content first.');
      return;
    }

    setParsing(true);
    setParseError(null);

    try {
      const response = await api.post('/dmc/parse', {
        raw_content: rawContent,
        arrival_city: formData.arrival_city,
        departure_city: formData.departure_city,
      });

      if (response.data.success) {
        const parsed = response.data.parsed_data;
        
        // Use new multi-option structure directly from backend
        // Backend now returns accommodation_options and pricing_options arrays
        let accommodationOptions = [];
        let pricingOptions = [];
        
        if (parsed.accommodation_options && parsed.accommodation_options.length > 0) {
          // New format: multiple options from backend
          accommodationOptions = parsed.accommodation_options.map(opt => ({
            option_name: opt.option_name || 'Standard',
            hotels: (opt.hotels || []).map(h => ({
              place: h.place || '',
              nights: h.nights || 1,
              rooms: 1,
              hotel: h.hotel || '',
              room_type: h.room_type || '',
              meal_plan: h.meal_plan || 'CP',
            }))
          }));
        } else if (parsed.accommodation && parsed.accommodation.length > 0) {
          // Backward compatibility: old flat accommodation format
          accommodationOptions = [{
            option_name: 'Standard',
            hotels: parsed.accommodation.map(acc => ({
              place: acc.place || '',
              nights: acc.nights || 1,
              rooms: 1,
              hotel: acc.hotel || '',
              room_type: acc.room_type || '',
              meal_plan: acc.meal_plan || 'CP',
            }))
          }];
        }
        
        if (parsed.pricing_options && parsed.pricing_options.length > 0) {
          // New format: multiple pricing options from backend
          pricingOptions = parsed.pricing_options.map(opt => ({
            option_name: opt.option_name || 'Standard',
            currency: opt.currency || 'INR',
            per_person: opt.per_person || 0,
            extra_adult: opt.extra_adult || 0,
            child_with_bed: opt.child_with_bed || 0,
            child_no_bed: opt.child_no_bed || 0,
            total_amount: 0,
          }));
        } else if (parsed.pricing) {
          // Backward compatibility: old flat pricing format
          pricingOptions = [{
            option_name: 'Standard',
            currency: 'INR',
            per_person: parsed.pricing.per_person || 0,
            extra_adult: parsed.pricing.extra_adult || 0,
            child_with_bed: parsed.pricing.child_with_bed || 0,
            child_no_bed: parsed.pricing.child_no_bed || 0,
            total_amount: 0,
          }];
        }
        
        // Ensure we have at least one option
        if (accommodationOptions.length === 0) {
          accommodationOptions = packageData.accommodation_options;
        }
        if (pricingOptions.length === 0) {
          pricingOptions = packageData.pricing_options;
        }
        
        setPackageData({
          package_title: parsed.package_title || '',
          total_nights: parsed.total_nights || 0,
          total_days: parsed.total_days || 0,
          accommodation_options: accommodationOptions,
          pricing_options: pricingOptions,
          itinerary: parsed.itinerary?.length > 0 ? parsed.itinerary : [{ day: 1, title: '', description: '' }],
          inclusions: parsed.inclusions?.length > 0 ? parsed.inclusions : [''],
          exclusions: parsed.exclusions?.length > 0 ? parsed.exclusions : [''],
          validity: parsed.validity || '',
          vehicle: parsed.vehicle || '',
          notes: parsed.notes || '',
        });
        
        // Update route from parsed accommodation data
        const parsedHotels = accommodationOptions[0]?.hotels || [];
        if (parsedHotels.length > 0) {
          const newRoute = parsedHotels.map((h, idx) => ({
            id: `stop-${Date.now()}-${idx}`,
            place_name: h.place || '',
            nights: h.nights || 1,
          }));
          setRoute(newRoute);
        }
        
        // Auto-fill passengers and rooms from parsed content if available
        const parsedPax = parsed.total_pax || 2;
        const parsedRooms = parsed.total_rooms || 1;
        
        // Only update if current values are default (2 adults, no children)
        if (formData.no_adults <= 2 && !formData.no_children_with_bed && !formData.no_children_no_bed) {
          handleInputChange('no_adults', parsedPax);
        }
        
        // Update room config if available
        if (parsedRooms > 0) {
          handleInputChange('double_rooms', parsedRooms);
          // Also update room_config object
          const currentRoomConfig = formData.room_config || {};
          handleInputChange('room_config', {
            ...currentRoomConfig,
            double_rooms: parsedRooms,
            auto_calculate: false, // User-specified rooms from DMC
          });
        }
        
        setParseError(null);
      } else {
        setParseError(response.data.error || 'Failed to parse content');
      }
    } catch (error) {
      setParseError(error.response?.data?.detail || 'Error parsing content. Please try again.');
    } finally {
      setParsing(false);
    }
  };

  // Handle file upload for PDF/Word documents
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
    const validExtensions = ['.pdf', '.docx', '.doc'];
    const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    
    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
      setParseError('Please upload a PDF (.pdf) or Word document (.docx)');
      return;
    }

    setExtractingFile(true);
    setParseError(null);
    setUploadedFile(file);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await api.post('/dmc/extract-file', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        // Set extracted text to the textarea
        setRawContent(response.data.extracted_text);
        setParseError(null);
      } else {
        setParseError(response.data.error || 'Failed to extract text from file');
        setUploadedFile(null);
      }
    } catch (error) {
      setParseError(error.response?.data?.detail || 'Error extracting text from file');
      setUploadedFile(null);
    } finally {
      setExtractingFile(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Clear uploaded file
  const clearUploadedFile = () => {
    setUploadedFile(null);
    setRawContent('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Update package data field
  const updateField = (field, value) => {
    setPackageData(prev => ({ ...prev, [field]: value }));
  };

  // ========== ACCOMMODATION HANDLERS ==========
  const addAccommodationOption = () => {
    const newOptionNum = packageData.accommodation_options.length + 1;
    setPackageData(prev => ({
      ...prev,
      accommodation_options: [
        ...prev.accommodation_options,
        {
          option_name: `Option ${newOptionNum}`,
          hotels: [{ place: '', nights: 1, rooms: 1, hotel: '', room_type: '', meal_plan: 'CP' }]
        }
      ]
    }));
  };

  const deleteAccommodationOption = (optionIdx) => {
    if (packageData.accommodation_options.length <= 1) return;
    setPackageData(prev => ({
      ...prev,
      accommodation_options: prev.accommodation_options.filter((_, i) => i !== optionIdx)
    }));
  };

  const addHotelRow = (optionIdx) => {
    setPackageData(prev => ({
      ...prev,
      accommodation_options: prev.accommodation_options.map((opt, i) => 
        i === optionIdx 
          ? { ...opt, hotels: [...opt.hotels, { place: '', nights: 1, rooms: 1, hotel: '', room_type: '', meal_plan: 'CP' }] }
          : opt
      )
    }));
  };

  const deleteHotelRow = (optionIdx, hotelIdx) => {
    setPackageData(prev => ({
      ...prev,
      accommodation_options: prev.accommodation_options.map((opt, i) => 
        i === optionIdx 
          ? { ...opt, hotels: opt.hotels.filter((_, j) => j !== hotelIdx) }
          : opt
      )
    }));
  };

  const updateHotel = (optionIdx, hotelIdx, field, value) => {
    setPackageData(prev => ({
      ...prev,
      accommodation_options: prev.accommodation_options.map((opt, i) => 
        i === optionIdx 
          ? {
              ...opt,
              hotels: opt.hotels.map((h, j) => 
                j === hotelIdx ? { ...h, [field]: value } : h
              )
            }
          : opt
      )
    }));
  };

  // ========== PRICING HANDLERS ==========
  const addPricingOption = () => {
    const newOptionNum = packageData.pricing_options.length + 1;
    setPackageData(prev => ({
      ...prev,
      pricing_options: [
        ...prev.pricing_options,
        { option_name: `Option ${newOptionNum}`, currency: 'INR', per_person: 0, extra_adult: 0, child_with_bed: 0, child_no_bed: 0, total_amount: 0 }
      ]
    }));
  };

  const deletePricingOption = (idx) => {
    if (packageData.pricing_options.length <= 1) return;
    setPackageData(prev => ({
      ...prev,
      pricing_options: prev.pricing_options.filter((_, i) => i !== idx)
    }));
  };

  const updatePricing = (idx, field, value) => {
    setPackageData(prev => ({
      ...prev,
      pricing_options: prev.pricing_options.map((p, i) => 
        i === idx ? { ...p, [field]: value } : p
      )
    }));
  };

  // ========== ITINERARY HANDLERS ==========
  const addItineraryDay = () => {
    const newDay = packageData.itinerary.length + 1;
    setPackageData(prev => ({
      ...prev,
      itinerary: [...prev.itinerary, { day: newDay, title: '', description: '' }]
    }));
  };

  const deleteItineraryDay = (idx) => {
    if (packageData.itinerary.length <= 1) return;
    setPackageData(prev => ({
      ...prev,
      itinerary: prev.itinerary.filter((_, i) => i !== idx).map((item, i) => ({ ...item, day: i + 1 }))
    }));
  };

  const updateItinerary = (idx, field, value) => {
    setPackageData(prev => ({
      ...prev,
      itinerary: prev.itinerary.map((item, i) => 
        i === idx ? { ...item, [field]: value } : item
      )
    }));
  };

  // ========== INCLUSIONS/EXCLUSIONS HANDLERS ==========
  const addInclusion = () => {
    setPackageData(prev => ({ ...prev, inclusions: [...prev.inclusions, ''] }));
  };

  const deleteInclusion = (idx) => {
    if (packageData.inclusions.length <= 1) return;
    setPackageData(prev => ({
      ...prev,
      inclusions: prev.inclusions.filter((_, i) => i !== idx)
    }));
  };

  const updateInclusion = (idx, value) => {
    setPackageData(prev => ({
      ...prev,
      inclusions: prev.inclusions.map((item, i) => i === idx ? value : item)
    }));
  };

  const addExclusion = () => {
    setPackageData(prev => ({ ...prev, exclusions: [...prev.exclusions, ''] }));
  };

  const deleteExclusion = (idx) => {
    if (packageData.exclusions.length <= 1) return;
    setPackageData(prev => ({
      ...prev,
      exclusions: prev.exclusions.filter((_, i) => i !== idx)
    }));
  };

  const updateExclusion = (idx, value) => {
    setPackageData(prev => ({
      ...prev,
      exclusions: prev.exclusions.map((item, i) => i === idx ? value : item)
    }));
  };

  // Check if we have valid data to show tables
  const hasData = entryMode === 'manual' || (packageData.package_title || packageData.accommodation_options[0]?.hotels[0]?.place);

  return (
    <div className="space-y-4">
      {/* Info Banner & Mode Toggle */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-sm text-slate-700">
              <strong className="text-purple-700">DMC Package</strong> - Create quotations from supplier packages
            </p>
          </div>
          <div className="flex items-center gap-3 bg-white rounded-lg px-3 py-2 border border-purple-200">
            <button
              onClick={() => setEntryMode('ai')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                entryMode === 'ai' 
                  ? 'bg-purple-600 text-white' 
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
              data-testid="dmc-mode-ai"
            >
              <Bot className="w-4 h-4" />
              AI Parse
            </button>
            <button
              onClick={() => setEntryMode('manual')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                entryMode === 'manual' 
                  ? 'bg-purple-600 text-white' 
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
              data-testid="dmc-mode-manual"
            >
              <PenLine className="w-4 h-4" />
              Manual Entry
            </button>
          </div>
        </div>
      </div>

      {/* AI Parse Section - Only shown in AI mode */}
      {entryMode === 'ai' && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="py-3 px-4 sm:px-6 bg-white border-b border-slate-200">
            <CardTitle className="text-sm font-bold text-[var(--navy)] flex items-center gap-2">
              <FileText className="w-4 h-4" />
              DMC Package Content
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 space-y-4">
            {/* File Upload Section */}
            <div className="space-y-3">
              <Label className="text-xs text-slate-600">
                Upload a PDF or Word document, or paste text directly below
              </Label>
              
              <div className="flex items-center gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.doc"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="dmc-file-upload"
                  data-testid="dmc-file-upload-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={extractingFile}
                  className="flex items-center gap-2"
                  data-testid="dmc-file-upload-btn"
                >
                  {extractingFile ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Extracting...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Upload PDF/Word
                    </>
                  )}
                </Button>
                
                {uploadedFile && (
                  <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-100 px-3 py-1.5 rounded-md">
                    <File className="w-4 h-4" />
                    <span className="truncate max-w-[200px]">{uploadedFile.name}</span>
                    <button
                      onClick={clearUploadedFile}
                      className="text-slate-400 hover:text-red-500 ml-1"
                      title="Clear file"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-white text-slate-400">or paste text below</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Textarea
                value={rawContent}
                onChange={(e) => setRawContent(e.target.value)}
                placeholder={`Paste the DMC package content here...

Example:
Kerala Delight 5N/6D

Day 1: Arrival Kochi - Munnar
Pick up from Kochi airport and drive to Munnar...

Hotels:
- Munnar (2N): Tea Valley Resort - Deluxe Room - MAP
- Thekkady (1N): Spice Garden - Standard Room - MAP

Cost: ₹18,500 per person on twin sharing
Extra Adult: ₹3,500

Inclusions:
- Accommodation as mentioned
- Meals as per plan

Exclusions:
- Airfare
- Personal expenses`}
                className="min-h-[180px] font-mono text-sm"
                data-testid="dmc-content-textarea"
              />
            </div>

            {parseError && (
              <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                <AlertCircle className="w-4 h-4" />
                {parseError}
              </div>
            )}

            <Button
              onClick={handleParse}
              disabled={parsing || !rawContent.trim()}
              className="w-full bg-purple-600 hover:bg-purple-700"
              data-testid="parse-dmc-btn"
            >
              {parsing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Parsing with AI...
                </>
              ) : (
                <>
                  <Bot className="w-4 h-4 mr-2" />
                  Parse Package Content
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Package Details Section - Show in Manual mode or after parsing */}
      {(entryMode === 'manual' || hasData) && (
        <Card className="border-green-200 shadow-sm bg-green-50/30">
          <CardHeader className="py-3 px-4 sm:px-6 bg-white border-b border-green-200">
            <CardTitle className="text-sm font-bold text-green-700 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Package Details
              {packageData.total_days > 0 && (
                <Badge variant="outline" className="ml-auto text-green-600 border-green-300">
                  {packageData.total_days}D / {packageData.total_nights}N
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          
          {/* Parse Status Summary - Shows after successful AI parse */}
          {entryMode !== 'manual' && hasData && packageData.package_title && (
            <div className="px-4 sm:px-6 py-2 bg-green-100 border-b border-green-200">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-green-800">
                <span className="font-semibold">✓ AI Parse Complete</span>
                <span>• {packageData.package_title.length > 40 ? packageData.package_title.substring(0, 40) + '...' : packageData.package_title}</span>
                {packageData.total_nights > 0 && <span>• {packageData.total_nights}N/{packageData.total_days}D</span>}
              </div>
            </div>
          )}
          
          <CardContent className="p-4 sm:p-6 space-y-6">
            {/* Pax Mismatch Warning */}
            {(() => {
              // Check if parsed pax differs from form pax
              const parsedPax = packageData.pricing_options?.[0]?.parsed_pax || 0;
              const formPax = (formData?.no_adults || 0) + (formData?.no_children_with_bed || 0) + (formData?.no_children_no_bed || 0);
              
              if (parsedPax > 0 && formPax > 0 && parsedPax !== formPax) {
                return (
                  <div className="flex items-start gap-2 text-amber-700 text-xs bg-amber-50 p-3 rounded-lg border border-amber-200">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold">Pax mismatch detected:</span> Source document shows {parsedPax} pax, but you've entered {formPax} pax. 
                      <span className="block mt-1">Pricing will be calculated based on your entered passengers ({formPax} pax).</span>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
            {/* Package Title */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Package Title</Label>
              <Input 
                value={packageData.package_title}
                onChange={(e) => updateField('package_title', e.target.value)}
                placeholder="Enter package title (e.g., Kerala Delight 5N/6D)"
                className="bg-white"
              />
            </div>

            {/* Accommodation Options */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold text-slate-500 uppercase flex items-center gap-1">
                  <Hotel className="w-3 h-3" /> Accommodation
                </Label>
                <Button variant="outline" size="sm" onClick={addAccommodationOption} className="h-7 text-xs">
                  <Plus className="w-3 h-3 mr-1" /> Add Option
                </Button>
              </div>
              
              {packageData.accommodation_options.map((option, optionIdx) => (
                <div key={optionIdx} className="border border-slate-200 rounded-lg bg-white overflow-hidden">
                  {/* Option Header */}
                  <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
                    <Input
                      value={option.option_name}
                      onChange={(e) => {
                        setPackageData(prev => ({
                          ...prev,
                          accommodation_options: prev.accommodation_options.map((opt, i) => 
                            i === optionIdx ? { ...opt, option_name: e.target.value } : opt
                          )
                        }));
                      }}
                      className="w-32 h-7 text-sm font-medium bg-white"
                    />
                    {packageData.accommodation_options.length > 1 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => deleteAccommodationOption(optionIdx)}
                        className="h-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                  
                  {/* Table Header */}
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-100 text-[10px] font-semibold text-slate-600 uppercase">
                    <div className="col-span-2">Place</div>
                    <div className="col-span-1">Nights</div>
                    <div className="col-span-1">Rooms</div>
                    <div className="col-span-3">Hotel</div>
                    <div className="col-span-2">Room Name</div>
                    <div className="col-span-2">Meal</div>
                    <div className="col-span-1"></div>
                  </div>
                  
                  {/* Table Rows */}
                  {option.hotels.map((hotel, hotelIdx) => (
                    <div key={hotelIdx} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-slate-100 items-center">
                      <div className="col-span-2">
                        <Input
                          value={hotel.place}
                          onChange={(e) => updateHotel(optionIdx, hotelIdx, 'place', e.target.value)}
                          placeholder="Place"
                          className="h-8 text-xs"
                        />
                      </div>
                      <div className="col-span-1">
                        <Input
                          type="number"
                          min="1"
                          value={hotel.nights}
                          onChange={(e) => updateHotel(optionIdx, hotelIdx, 'nights', parseInt(e.target.value) || 1)}
                          className="h-8 text-xs text-center"
                        />
                      </div>
                      <div className="col-span-1">
                        <Input
                          type="number"
                          min="1"
                          value={hotel.rooms}
                          onChange={(e) => updateHotel(optionIdx, hotelIdx, 'rooms', parseInt(e.target.value) || 1)}
                          className="h-8 text-xs text-center"
                        />
                      </div>
                      <div className="col-span-3">
                        <Input
                          value={hotel.hotel}
                          onChange={(e) => updateHotel(optionIdx, hotelIdx, 'hotel', e.target.value)}
                          placeholder="Hotel name"
                          className="h-8 text-xs"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          value={hotel.room_type}
                          onChange={(e) => updateHotel(optionIdx, hotelIdx, 'room_type', e.target.value)}
                          placeholder="Room type"
                          className="h-8 text-xs"
                        />
                      </div>
                      <div className="col-span-2">
                        <Select
                          value={hotel.meal_plan}
                          onValueChange={(v) => updateHotel(optionIdx, hotelIdx, 'meal_plan', v)}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {MEAL_PLANS.map(mp => (
                              <SelectItem key={mp} value={mp}>{mp}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-1 flex justify-center">
                        {option.hotels.length > 1 && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => deleteHotelRow(optionIdx, hotelIdx)}
                            className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {/* Add Row Button */}
                  <div className="px-3 py-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => addHotelRow(optionIdx)}
                      className="h-7 text-xs text-slate-600 hover:text-slate-800"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Add Row
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pricing Options */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold text-slate-500 uppercase flex items-center gap-1">
                  <DollarSign className="w-3 h-3" /> Base Pricing (Cost from DMC)
                </Label>
                <Button variant="outline" size="sm" onClick={addPricingOption} className="h-7 text-xs">
                  <Plus className="w-3 h-3 mr-1" /> Add Option
                </Button>
              </div>
              
              <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-100 text-[10px] font-semibold text-slate-600 uppercase">
                  <div className="col-span-2">Option</div>
                  <div className="col-span-1">Currency</div>
                  <div className="col-span-2">Per Person</div>
                  <div className="col-span-2">Extra Adult</div>
                  <div className="col-span-1">Child+Bed</div>
                  <div className="col-span-1">Child-Bed</div>
                  <div className="col-span-2">Total Amount</div>
                  <div className="col-span-1"></div>
                </div>
                
                {/* Pricing Rows */}
                {packageData.pricing_options.map((pricing, idx) => {
                  const hasPassengers = (formData.no_adults || 0) > 0;
                  const calculatedTotal = calculateTotalAmount(pricing);
                  const currencySymbol = CURRENCIES.find(c => c.value === pricing.currency)?.symbol || '₹';
                  
                  return (
                    <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-slate-100 items-center">
                      <div className="col-span-2">
                        <Input
                          value={pricing.option_name}
                          onChange={(e) => updatePricing(idx, 'option_name', e.target.value)}
                          className="h-8 text-xs"
                        />
                      </div>
                      <div className="col-span-1">
                        <Select
                          value={pricing.currency}
                          onValueChange={(v) => updatePricing(idx, 'currency', v)}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CURRENCIES.map(c => (
                              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          value={pricing.per_person || ''}
                          onChange={(e) => updatePricing(idx, 'per_person', parseFloat(e.target.value) || 0)}
                          placeholder="0"
                          className="h-8 text-xs"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          value={pricing.extra_adult || ''}
                          onChange={(e) => updatePricing(idx, 'extra_adult', parseFloat(e.target.value) || 0)}
                          placeholder="0"
                          className="h-8 text-xs"
                        />
                      </div>
                      <div className="col-span-1">
                        <Input
                          type="number"
                          value={pricing.child_with_bed || ''}
                          onChange={(e) => updatePricing(idx, 'child_with_bed', parseFloat(e.target.value) || 0)}
                          placeholder="0"
                          className="h-8 text-xs"
                        />
                      </div>
                      <div className="col-span-1">
                        <Input
                          type="number"
                          value={pricing.child_no_bed || ''}
                          onChange={(e) => updatePricing(idx, 'child_no_bed', parseFloat(e.target.value) || 0)}
                          placeholder="0"
                          className="h-8 text-xs"
                        />
                      </div>
                      <div className="col-span-2">
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            value={pricing.total_amount || (hasPassengers ? calculatedTotal : '')}
                            onChange={(e) => updatePricing(idx, 'total_amount', parseFloat(e.target.value) || 0)}
                            placeholder={hasPassengers ? calculatedTotal.toString() : "0"}
                            className="h-8 text-xs"
                          />
                          {hasPassengers && !pricing.total_amount && (
                            <span className="text-[9px] text-slate-400 whitespace-nowrap">(auto)</span>
                          )}
                        </div>
                      </div>
                      <div className="col-span-1 flex justify-center">
                        {packageData.pricing_options.length > 1 && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => deletePricingOption(idx)}
                            className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <p className="text-[10px] text-slate-500">
                Total = (Per Person × Twin Adults) + (Extra Adult × Extra Adults) + (Child rates × Kids).
                {!(formData.no_adults > 0) && " Fill passenger details to auto-calculate."}
              </p>
              
              {/* Currency Conversion - Show only when non-INR currency is used */}
              {packageData.pricing_options.some(p => p.currency && p.currency !== 'INR') && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-4 h-4 text-amber-600" />
                    <span className="text-xs font-semibold text-amber-800">Currency Conversion</span>
                  </div>
                  <p className="text-[10px] text-amber-700 mb-2">
                    Foreign currency pricing detected. Enter conversion rate to INR for final quotation.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {[...new Set(packageData.pricing_options.map(p => p.currency).filter(c => c && c !== 'INR'))].map(currency => {
                      const currencyInfo = CURRENCIES.find(c => c.value === currency);
                      const conversionRateKey = `${currency.toLowerCase()}_to_inr_rate`;
                      const currentRate = packageData[conversionRateKey] || 0;
                      
                      return (
                        <div key={currency} className="flex items-center gap-2">
                          <Label className="text-xs text-amber-800 whitespace-nowrap">
                            1 {currencyInfo?.symbol || currency} =
                          </Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={currentRate || ''}
                            onChange={(e) => {
                              const rate = parseFloat(e.target.value) || 0;
                              setPackageData(prev => ({
                                ...prev,
                                [conversionRateKey]: rate
                              }));
                            }}
                            placeholder="Enter rate"
                            className="h-7 text-xs w-24"
                          />
                          <span className="text-xs text-amber-700">₹ INR</span>
                        </div>
                      );
                    })}
                  </div>
                  {packageData.pricing_options.some(p => p.currency !== 'INR' && !packageData[`${p.currency?.toLowerCase()}_to_inr_rate`]) && (
                    <p className="text-[10px] text-red-600 mt-2 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Please enter conversion rate(s) before saving the quotation.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Route Builder Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold text-slate-500 uppercase flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Route ({route.length} stops • {totalDaysFromRoute}D / {totalNightsFromRoute}N)
                </Label>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={addRouteStop} className="h-7 text-xs">
                    <Plus className="w-3 h-3 mr-1" /> Add Stop
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setExpandedRoute(!expandedRoute)}
                    className="h-7 text-xs"
                  >
                    {expandedRoute ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              
              {expandedRoute && (
                <div className="space-y-2">
                  {route.map((stop, idx) => (
                    <div key={stop.id} className="flex items-center gap-2 p-2 border border-slate-200 rounded-lg bg-white">
                      <div className="w-6 h-6 bg-[var(--navy)] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {idx + 1}
                      </div>
                      <Input
                        value={stop.place_name}
                        onChange={(e) => updateRouteStop(idx, 'place_name', e.target.value)}
                        placeholder="Enter place name"
                        className="flex-1 h-8 text-sm"
                      />
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          min="0"
                          value={stop.nights}
                          onChange={(e) => updateRouteStop(idx, 'nights', e.target.value)}
                          className="w-14 h-8 text-sm text-center"
                        />
                        <span className="text-xs text-gray-500">N</span>
                      </div>
                      {route.length > 1 && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeRouteStop(idx)}
                          className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                  
                  {/* Route Summary with pills */}
                  <div className="flex flex-wrap items-center gap-1 p-2 bg-slate-50 rounded-lg border border-slate-200">
                    <span className="text-xs text-gray-500 mr-1">Route:</span>
                    {formData.arrival_city && (
                      <>
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs px-2">
                          A: {formData.arrival_city}
                        </Badge>
                        <ArrowRight className="w-3 h-3 text-gray-400" />
                      </>
                    )}
                    {route.map((stop, idx) => (
                      <React.Fragment key={stop.id}>
                        {stop.place_name && (
                          <>
                            <Badge className="bg-[var(--navy)]/10 text-[var(--navy)] border-[var(--navy)]/20 text-xs px-2">
                              {stop.place_name} ({stop.nights}N)
                            </Badge>
                            {idx < route.length - 1 && <ArrowRight className="w-3 h-3 text-gray-400" />}
                          </>
                        )}
                      </React.Fragment>
                    ))}
                    {formData.departure_city && (
                      <>
                        <ArrowRight className="w-3 h-3 text-gray-400" />
                        <Badge className="bg-red-100 text-red-700 border-red-200 text-xs px-2">
                          D: {formData.departure_city}
                        </Badge>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Itinerary */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold text-slate-500 uppercase flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Itinerary ({packageData.itinerary.length} days)
                </Label>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={autoGenerateItinerary}
                    className="h-7 text-xs bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200 text-purple-700 hover:from-purple-100 hover:to-blue-100"
                  >
                    <Sparkles className="w-3 h-3 mr-1" /> Auto-Generate
                  </Button>
                  <Button variant="outline" size="sm" onClick={addItineraryDay} className="h-7 text-xs">
                    <Plus className="w-3 h-3 mr-1" /> Add Day
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setExpandedItinerary(!expandedItinerary)}
                    className="h-7 text-xs"
                  >
                    {expandedItinerary ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              
              {expandedItinerary && (
                <div className="space-y-2">
                  {packageData.itinerary.map((day, idx) => (
                    <div key={idx} className="border border-slate-200 rounded-lg bg-white p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-[var(--navy)] text-white h-6">Day {day.day}</Badge>
                        <Input
                          value={day.title}
                          onChange={(e) => updateItinerary(idx, 'title', e.target.value)}
                          placeholder="Day title (e.g., Arrival Kochi - Munnar)"
                          className="flex-1 h-8 text-sm font-medium"
                        />
                        <Select 
                          value={day.overnight_location || ''} 
                          onValueChange={(v) => updateItinerary(idx, 'overnight_location', v)}
                        >
                          <SelectTrigger className="w-36 h-8 text-xs">
                            <SelectValue placeholder="Overnight" />
                          </SelectTrigger>
                          <SelectContent>
                            {route.filter(s => s.place_name).map(stop => (
                              <SelectItem key={stop.id} value={stop.place_name}>
                                {stop.place_name}
                              </SelectItem>
                            ))}
                            <SelectItem value="Departure">Departure</SelectItem>
                          </SelectContent>
                        </Select>
                        {packageData.itinerary.length > 1 && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => deleteItineraryDay(idx)}
                            className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                      <Textarea
                        value={day.description}
                        onChange={(e) => updateItinerary(idx, 'description', e.target.value)}
                        placeholder="Day description and activities..."
                        className="min-h-[60px] text-xs"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Inclusions & Exclusions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Inclusions */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold text-green-600 uppercase flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Inclusions
                  </Label>
                  <Button variant="ghost" size="sm" onClick={addInclusion} className="h-6 text-xs text-green-600">
                    <Plus className="w-3 h-3 mr-1" /> Add
                  </Button>
                </div>
                <div className="space-y-1.5">
                  {packageData.inclusions.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Check className="w-3 h-3 text-green-500 flex-shrink-0" />
                      <Input
                        value={item}
                        onChange={(e) => updateInclusion(idx, e.target.value)}
                        placeholder="Enter inclusion..."
                        className="h-7 text-xs flex-1"
                      />
                      {packageData.inclusions.length > 1 && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => deleteInclusion(idx)}
                          className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Exclusions */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold text-red-600 uppercase flex items-center gap-1">
                    <XCircle className="w-3 h-3" /> Exclusions
                  </Label>
                  <Button variant="ghost" size="sm" onClick={addExclusion} className="h-6 text-xs text-red-600">
                    <Plus className="w-3 h-3 mr-1" /> Add
                  </Button>
                </div>
                <div className="space-y-1.5">
                  {packageData.exclusions.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <XCircle className="w-3 h-3 text-red-400 flex-shrink-0" />
                      <Input
                        value={item}
                        onChange={(e) => updateExclusion(idx, e.target.value)}
                        placeholder="Enter exclusion..."
                        className="h-7 text-xs flex-1"
                      />
                      {packageData.exclusions.length > 1 && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => deleteExclusion(idx)}
                          className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Validity</Label>
                <Input
                  value={packageData.validity}
                  onChange={(e) => updateField('validity', e.target.value)}
                  placeholder="e.g., Apr 2026 - Sep 2026"
                  className="h-8 text-xs"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Vehicle</Label>
                <Input
                  value={packageData.vehicle}
                  onChange={(e) => updateField('vehicle', e.target.value)}
                  placeholder="e.g., Innova, Tempo"
                  className="h-8 text-xs"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Notes</Label>
                <Input
                  value={packageData.notes}
                  onChange={(e) => updateField('notes', e.target.value)}
                  placeholder="Additional notes..."
                  className="h-8 text-xs"
                />
              </div>
            </div>

            {/* BUG-17 — Misc Cost editor for DMC packages. Outputs to
                top-level formData.misc_costs which is the same shape Manual /
                Custom / Predefined writes. The CreateQuotation dmcPayload
                must include `misc_costs: formData.misc_costs || []` to
                propagate to the backend. */}
            <div className="space-y-2 pt-3 border-t border-slate-200" data-testid="dmc-misc-costs-editor">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-navy">Miscellaneous Costs</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const next = [
                      ...(formData.misc_costs || []),
                      {
                        id: `mc-${Date.now()}`,
                        remarks: '',
                        cost_type: 'fixed',
                        amount: 0,
                        gst_percentage: 0,
                        is_optional: false,
                      },
                    ];
                    setFormData((p) => ({ ...p, misc_costs: next }));
                  }}
                  className="h-7 text-xs"
                  data-testid="dmc-add-misc-cost-btn"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
              {(formData.misc_costs || []).length === 0 ? (
                <p className="text-xs text-gray-500 italic">No miscellaneous costs added</p>
              ) : (
                <div className="space-y-1.5">
                  {(formData.misc_costs || []).map((cost, idx) => (
                    <div
                      key={cost.id || idx}
                      className={`p-2 rounded border ${cost.is_optional ? 'border-amber-300 bg-amber-50/40' : 'border-green-300 bg-green-50/40'}`}
                    >
                      <div className="flex flex-col lg:flex-row gap-2 items-end">
                        <label className="flex items-center gap-1.5 text-[10px] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!cost.is_optional}
                            onChange={(e) => {
                              const next = (formData.misc_costs || []).slice();
                              next[idx] = { ...cost, is_optional: !e.target.checked };
                              setFormData((p) => ({ ...p, misc_costs: next }));
                            }}
                            className="w-3 h-3"
                          />
                          {!cost.is_optional ? 'Inc' : 'Opt'}
                        </label>
                        <Input
                          placeholder="Description"
                          value={cost.remarks || ''}
                          onChange={(e) => {
                            const next = (formData.misc_costs || []).slice();
                            next[idx] = { ...cost, remarks: e.target.value };
                            setFormData((p) => ({ ...p, misc_costs: next }));
                          }}
                          className="h-7 text-xs flex-1"
                        />
                        <select
                          value={cost.cost_type || 'fixed'}
                          onChange={(e) => {
                            const next = (formData.misc_costs || []).slice();
                            next[idx] = { ...cost, cost_type: e.target.value };
                            setFormData((p) => ({ ...p, misc_costs: next }));
                          }}
                          className="h-7 text-xs border rounded px-1 w-24"
                        >
                          <option value="fixed">Fixed</option>
                          <option value="per_person">Per Person</option>
                          <option value="per_room">Per Room</option>
                          <option value="per_night">Per Night</option>
                        </select>
                        <Input
                          type="number"
                          min="0"
                          placeholder="₹"
                          value={cost.amount || 0}
                          onChange={(e) => {
                            const next = (formData.misc_costs || []).slice();
                            next[idx] = { ...cost, amount: parseFloat(e.target.value) || 0 };
                            setFormData((p) => ({ ...p, misc_costs: next }));
                          }}
                          className="h-7 text-xs w-20"
                        />
                        <select
                          value={String(cost.gst_percentage ?? 0)}
                          onChange={(e) => {
                            const next = (formData.misc_costs || []).slice();
                            next[idx] = { ...cost, gst_percentage: parseFloat(e.target.value) || 0 };
                            setFormData((p) => ({ ...p, misc_costs: next }));
                          }}
                          className="h-7 text-xs border rounded px-1 w-16"
                        >
                          <option value="0">No GST</option>
                          <option value="5">5%</option>
                          <option value="12">12%</option>
                          <option value="18">18%</option>
                        </select>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            const next = (formData.misc_costs || []).filter((_, i) => i !== idx);
                            setFormData((p) => ({ ...p, misc_costs: next }));
                          }}
                          className="h-7 w-7 p-0 text-red-500"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DMCPackageBuilder;
